import { Alert, Button, Divider, Skeleton, Tooltip } from 'antd'
import { ErrorScreen } from 'components/ErrorScreen'
import { useGeckoNotifications } from 'features/notifications'
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import { useTicketLifecycle } from './lifecycle/TicketLifecycleContext'
import { useStepNavigation } from './StepNavigationContext'
import { useNavigationGuard, useStepValidation } from './StepValidationContext'
import { useTicketAccess } from './ticket/TicketAccessContext'
import { useTicketStepContext } from './ticket/TicketStateContext'
import { useInactiveStepIds } from './ticket/useInactiveStepIds'
import { getSkippingStepIds } from './workflow/inactiveSteps'
import {
  LibraWorflowStepComponentProps,
  LibraWorkflowStepProps,
  StepValidationResult,
  UseValidateConfigHook,
} from './workflow/workflowStepsApi'
import { useWorkflow } from './WorkflowContext'

type StepModule = {
  Component: FC<LibraWorflowStepComponentProps<Record<string, unknown>>>
  useValidateConfig?: UseValidateConfigHook
}

type LoadedStepModule = {
  componentName: string
  module: StepModule
}

const useAlwaysValid: UseValidateConfigHook = () => true

export const WorkflowStepContainer: FC<{
  componentName: string
  config: Record<string, unknown>
}> = ({ componentName, config }) => {
  const [loadedStep, setLoadedStep] = useState<LoadedStepModule | null>(null)

  useEffect(() => {
    let isActive = true
    setLoadedStep(null)

    import(`./stepComponents/${componentName}.tsx`)
      .then((module) => {
        if (!isActive) {
          return
        }
        setLoadedStep({
          componentName,
          module: {
            Component: module.Component as StepModule['Component'],
            useValidateConfig: (module.useValidateConfig ??
              useAlwaysValid) as StepModule['useValidateConfig'],
          },
        })
      })
      .catch((error) => {
        console.error('Failed to load workflow step module', error)
      })

    return () => {
      isActive = false
    }
  }, [componentName])

  if (!loadedStep || loadedStep.componentName !== componentName) {
    return <Skeleton active paragraph={{ rows: 10 }} />
  }

  return (
    <LoadedWorkflowStep
      key={componentName}
      module={loadedStep.module}
      config={config}
      componentName={componentName}
    />
  )
}

export const LoadedWorkflowStep: FC<{
  module: StepModule
  config: Record<string, unknown>
  componentName: string
}> = ({ module, config, componentName }) => {
  const { advanceTicketState, readOnly, canEditFields, fieldValues } =
    useTicketAccess()
  const workflow = useWorkflow()
  const inactiveStepIds = useInactiveStepIds()
  const { completeWorkflow, workflowCompleteLabel, canCompleteWorkflow } =
    useTicketLifecycle()
  const { Component, useValidateConfig } = module
  const isValidConfig = (useValidateConfig ?? useAlwaysValid)({
    config,
    name: componentName,
  } satisfies LibraWorkflowStepProps<Record<string, unknown>>)

  const { validationResult, setValidationResult } = useStepValidation()
  // Layout phase, so the reset lands before the new step reports its result.
  useLayoutEffect(() => {
    setValidationResult(undefined)
  }, [componentName, module, setValidationResult])
  const {
    activeStepId,
    isActiveStep,
    isStepInactive,
    hasMoreSteps,
    isWorkflowComplete,
  } = useTicketStepContext()
  const skippingStepName = useMemo(() => {
    const skippingStepId = getSkippingStepIds(workflow, fieldValues).get(
      activeStepId
    )
    return workflow.steps.find((step) => step.id === skippingStepId)?.name
  }, [workflow, fieldValues, activeStepId])
  const { activeStepIndex, setActiveStepIndex } = useStepNavigation()
  const { guardNavigation } = useNavigationGuard()
  const { errorNotification } = useGeckoNotifications()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const handleAdvance = useCallback(() => {
    if (!isActiveStep) {
      if (hasMoreSteps) {
        const nextActiveIndex = workflow.steps.findIndex(
          (step, index) =>
            index > activeStepIndex && !inactiveStepIds.has(step.id)
        )
        if (nextActiveIndex >= 0) setActiveStepIndex(nextActiveIndex)
      }
      return
    }
    if (isWorkflowComplete) {
      return
    }
    const proceed = async () => {
      setIsSubmitting(true)
      try {
        if (!hasMoreSteps) {
          await completeWorkflow()
        } else {
          await advanceTicketState(activeStepId)
        }
      } catch (err) {
        errorNotification(
          `Failed to ${hasMoreSteps ? 'advance' : 'complete'} the workflow: ${
            err instanceof Error ? err.message : String(err)
          }`
        )
      } finally {
        if (mountedRef.current) setIsSubmitting(false)
      }
    }
    guardNavigation(() => void proceed())
  }, [
    advanceTicketState,
    completeWorkflow,
    guardNavigation,
    activeStepIndex,
    setActiveStepIndex,
    activeStepId,
    inactiveStepIds,
    workflow.steps,
    isActiveStep,
    isWorkflowComplete,
    hasMoreSteps,
    errorNotification,
  ])

  const buttonLabel = hasMoreSteps ? 'Next' : workflowCompleteLabel
  const isButtonDisabled = isActiveStep
    ? readOnly ||
      Boolean(validationResult) ||
      isWorkflowComplete ||
      (!hasMoreSteps && !canEditFields) ||
      (!hasMoreSteps && !canCompleteWorkflow)
    : !hasMoreSteps

  const tooltipTitle = useMemo(
    () =>
      getActionsTooltip(
        isActiveStep,
        hasMoreSteps,
        validationResult,
        isWorkflowComplete,
        readOnly,
        canEditFields,
        canCompleteWorkflow
      ),
    [
      isActiveStep,
      hasMoreSteps,
      validationResult,
      isWorkflowComplete,
      readOnly,
      canEditFields,
      canCompleteWorkflow,
    ]
  )

  return isValidConfig ? (
    <>
      {isStepInactive && (
        <NotApplicableBanner
          type='info'
          showIcon
          message={
            skippingStepName
              ? `Not applicable — the ${skippingStepName} outcome makes this step not applicable`
              : "Not applicable — an earlier step's outcome makes this step not applicable"
          }
        />
      )}
      <Component
        config={config}
        name={componentName}
        setValidationResult={setValidationResult}
        readonly={readOnly || isStepInactive}
      />
      <StyledDivider />
      <ActionsContainer>
        <Tooltip placement='topLeft' title={tooltipTitle}>
          <Button
            type='default'
            onClick={handleAdvance}
            loading={isSubmitting}
            disabled={isButtonDisabled}>
            {buttonLabel}
          </Button>
        </Tooltip>
      </ActionsContainer>
    </>
  ) : (
    <ErrorScreen
      title='Invalid Configuration'
      message='The configuration for this step failed validation.'
    />
  )
}

function getActionsTooltip(
  isActiveStep: boolean,
  hasMoreSteps: boolean,
  validationResult: StepValidationResult,
  isWorkflowComplete: boolean,
  readOnly: boolean,
  canEditFields: boolean,
  canCompleteWorkflow: boolean
): string {
  if (!isActiveStep && hasMoreSteps) {
    return 'Next step'
  }
  if (readOnly) {
    return 'You do not have permission to edit this ticket'
  }
  if (isWorkflowComplete) {
    return 'Workflow complete'
  }
  if (!isActiveStep) {
    return 'This is not the active step'
  }
  if (validationResult) {
    return validationResult.message
  }
  if (!hasMoreSteps && !canEditFields) {
    return 'You do not have permission to complete this workflow'
  }
  if (!hasMoreSteps && !canCompleteWorkflow) {
    return 'Save your progress before submitting'
  }
  if (!hasMoreSteps) {
    return 'Complete workflow'
  }
  return 'Next step'
}

const NotApplicableBanner = styled(Alert)`
  margin-bottom: 1rem;
`

const StyledDivider = styled(Divider)`
  padding: 0.2rem;
`

const ActionsContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  padding-right: 1rem;
`
